<template>
    <v-container>
        <!-- Loader -->
        <div v-if="loading" class="text-center mt-10">
            <v-progress-circular indeterminate color="primary" />
        </div>

        <!-- No Records Found -->
        <div v-else-if="bots.length === 0" class="text-center my-4 text-medium-emphasis">
            No records found.
        </div>

        <!-- Bot Cards -->
        <v-row v-else>
            <v-col v-for="(bot, index) in bots" :key="index" cols="12" md="6" class="d-flex">
                <v-card class="w-100 pa-4 bot-card" elevation="2">
                    <!-- Bot Name and Icon -->
                    <v-card-title class="d-flex align-center mb-2 text-grey-darken-3">
                        <span class="me-2">
                            <i v-if="bot.Bot_Type === 'Chat Agent'" class="fa-solid fa-message icon-chat"></i>
                            <i v-else-if="bot.Bot_Type === 'Voice Agent'" class="fa-solid fa-phone-volume icon-voice"></i>
                        </span>
                        <h3 class="header">{{ bot.Bot_Name }}</h3>
                    </v-card-title>

                    <!-- Bot Info -->
                    <v-card-text class="bot-details text-body-2 text-grey-darken-1 text-start">
                        <div class="mb-1">
                            <strong class="text-grey-darken-2">AI Agent Type:</strong> {{ bot.Bot_Type }}
                        </div>
                        
                        <div class="mb-1">
                            <strong class="text-grey-darken-2">Subscription Start Date:</strong>
                            {{ new Date(bot.Subscription_Start_Date).toLocaleDateString() }}
                        </div>
                    </v-card-text>

                    <!-- Actions -->
                    <v-card-actions class="pt-4 text-start ps-0">
                        <v-btn v-for="action in getBotActions(bot)" :key="action" class="me-2 rounded-pill px-4"
                            variant="flat" style="text-transform: none;" @click="handleAction(action, bot)">
                            {{ action }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { medicalBotIds } from "@/config/medicalBots.js"


const router = useRouter()

// Reactive customer info from localStorage
const customerGUID = localStorage.getItem('customerGUID');

const bots = ref([])
const loading = ref(true)
const actions = ['CONVERSATION HISTORY', 'ANALYTICS']

const getBotActions = (bot) => {
    if (medicalBotIds.includes(bot.BotID)) {
        return actions.filter(action => action !== 'CONVERSATION HISTORY');
    }
    return actions;
}


const fetchBots = async () => {
    if (!customerGUID) {
        loading.value = false
        return
    }

    loading.value = true
    try {
        const response = await fetch(`${process.env.VUE_APP_VORTEX_PORTAL_WS}/bots/all`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 'customerGUID': customerGUID.trim() })
        })

        if (!response.ok) {
            throw new Error(`API request failed with status ${response.status}`)
        }

        const data = await response.json()
        bots.value = data.bots || []
    } catch (error) {
        console.error('Fetch bots error:', error)
        bots.value = []
    } finally {
        loading.value = false
    }
}

// Handle bot action clicks
const handleAction = (action, bot) => {
    // Save selected bot details to local storage
    localStorage.setItem('botName', bot.Bot_Name)
    localStorage.setItem('botID', bot.BotID)
    localStorage.setItem('botType', bot.Bot_Type)
    localStorage.setItem('botDID', bot.Bot_DID)

    if (action === 'CONVERSATION HISTORY') {
        router.push('/distributor/conversation-history/voice')
    } else if (action === 'ANALYTICS') {
        router.push({
            path: '/distributor/analytics',
            query: {
                ClientID: customerGUID.value,
                BotID: bot.BotID // Use BotID from API response
            }
        })
    }
}
// Load FontAwesome and fetch bots on mount
onMounted(() => {
    if (!document.querySelector('link[href*="font-awesome"]')) {
        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css'
        document.head.appendChild(link)
    }

    window.addEventListener('storage', (event) => {
        if (event.key === 'customerGUID') {
            customerGUID.value = event.newValue || '';
        }
    });

    fetchBots()
})

watch(customerGUID, (newGuid) => {
    if (newGuid) {
        fetchBots()
    }
})




</script>


<style scoped>
.bot-card {
    border-radius: 12px;
    transition: 0.3s ease;
    background-color: #f9f9f9;
}

.bot-card:hover {
    box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.12);
}

.bot-details {
    line-height: 1.6;
}

i {
    font-size: 1.3rem;
}

.icon-chat {
    color: black;
}

.icon-voice {
    color: black;
}

h3.header {
    font-family: 'Montserrat', sans-serif;
    font-size: 20px;
    font-weight: 400;
}

button {
    background-color: #788e90;
    color: white;
    font-size: 14px;
}
</style>
